import { t } from "@travily/i18n";
import { RangeSlider } from "@ui/components/RangeSlider";
import { tv } from "@ui/lib/tv";
import { useCallback, useEffect, useState } from "react";

function parseNumeric(raw: string) {
  return Number.parseInt(raw.replace(/[^\d]/g, ""), 10);
}

const priceRange = tv({
  slots: {
    root: ["relative flex flex-col gap-6 overflow-clip rounded-3xl bg-white p-7", "shadow-keyline-inset"],
    chart: "flex flex-col",
    histogram: "pointer-events-none flex h-16 items-end gap-px",
    bar: "flex-1 rounded-t",
    inputs: "flex items-start justify-between",
    inputGroup: "flex flex-col items-start gap-2",
    inputLabel: "w-full text-center text-base text-neutral-900",
    input: [
      "flex h-12 w-[100px] cursor-text items-center justify-center rounded-full text-base text-neutral-900",
      "shadow-keyline-inset",
      "focus-within:ring-2 focus-within:ring-red-400",
    ],
    inputPrefix: "select-none",
    inputSizer: "relative",
    inputMirror: "invisible whitespace-pre",
    inputField: ["absolute inset-0 w-full bg-transparent p-0 text-inherit", "caret-red-400 outline-none"],
  },
  variants: {
    selected: {
      true: { bar: "bg-red-400" },
      false: { bar: "bg-neutral-200" },
    },
  },
});

type PriceRangeStyles = ReturnType<typeof priceRange>;

interface PriceInputProps {
  label: string;
  local: string;
  onCommit: () => void;
  onEdit: (editing: boolean) => void;
  onLocalChange: (raw: string) => void;
  prefix: string;
  styles: PriceRangeStyles;
}

// The currency symbol is presentation, not value: it sits outside the input so
// it can't be selected, edited, or deleted, and the whole pill is a <label> so
// clicking anywhere in it (symbol included) drops the caret into the field.
// The invisible mirror gives the field its text's exact width, keeping the
// symbol flush against the number while the pair stays centered in the pill.
function PriceInput({ label, local, onCommit, onEdit, onLocalChange, prefix, styles }: PriceInputProps) {
  // A cleared field keeps one digit's width so the pill never collapses.
  const mirrorText = local.length > 0 ? local : "0";

  return (
    <label className={styles.input()}>
      <span aria-hidden="true" className={styles.inputPrefix()}>
        {prefix}
      </span>
      <span className={styles.inputSizer()}>
        <span aria-hidden="true" className={styles.inputMirror()}>
          {mirrorText}
        </span>
        <input
          aria-label={label}
          className={styles.inputField()}
          inputMode="numeric"
          onBlur={() => {
            onEdit(false);
            onCommit();
          }}
          onChange={(e) => onLocalChange(e.target.value)}
          onFocus={() => onEdit(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onCommit();
          }}
          value={local}
        />
      </span>
    </label>
  );
}

export interface PriceRangeProps {
  className?: string;
  currencySymbol?: string;
  /** Raw counts, one bar per element, over equal-width buckets spanning [min, max].
   *  Bucket i covers [min + i*w, min + (i+1)*w) where w = (max - min) / length. */
  distribution?: number[];
  max: number;
  min: number;
  /** Fired on slider release and on input commit (blur/Enter) — once per gesture. */
  onChange: (range: [number, number]) => void;
  step?: number;
  value: [number, number];
}

export function PriceRange({
  className,
  currencySymbol = "$",
  distribution,
  max,
  min,
  onChange,
  step,
  value,
}: PriceRangeProps) {
  const styles = priceRange();
  // Thumbs and inputs track this live value while dragging; the parent only
  // hears about it on release, so a refine-per-frame can't hammer the backend.
  const [dragValue, setDragValue] = useState<[number, number] | null>(null);
  const displayValue = dragValue ?? value;
  const [localMin, setLocalMin] = useState(String(displayValue[0]));
  const [localMax, setLocalMax] = useState(String(displayValue[1]));
  const [editingMin, setEditingMin] = useState(false);
  const [editingMax, setEditingMax] = useState(false);

  useEffect(() => {
    if (!editingMin) setLocalMin(String(displayValue[0]));
    if (!editingMax) setLocalMax(String(displayValue[1]));
  }, [displayValue, editingMin, editingMax]);

  const commitMin = useCallback(() => {
    const parsed = parseNumeric(localMin);
    const clamped = Number.isNaN(parsed) ? min : Math.max(min, Math.min(parsed, value[1]));
    setLocalMin(String(clamped));
    onChange([clamped, value[1]]);
  }, [localMin, min, value, onChange]);

  const commitMax = useCallback(() => {
    const parsed = parseNumeric(localMax);
    const clamped = Number.isNaN(parsed) ? max : Math.min(max, Math.max(parsed, value[0]));
    setLocalMax(String(clamped));
    onChange([value[0], clamped]);
  }, [localMax, max, value, onChange]);

  const minimumLabel = t("components.price_range.minimum_label");
  const maximumLabel = t("components.price_range.maximum_label");

  const bars = distribution && distribution.length > 0 ? distribution : null;
  const maxCount = bars ? Math.max(...bars, 1) : 1;
  const bucketWidth = bars ? (max - min) / bars.length : 0;

  return (
    <div className={styles.root({ className })}>
      <div className={styles.chart()}>
        {bars ? (
          // Bars render before the slider so its thumbs paint on top; the track
          // reads as their baseline. Flat bottoms and zero height for empty
          // buckets keep the bars growing out of the track rather than
          // floating above it.
          <div aria-hidden="true" className={styles.histogram()}>
            {bars.map((count, index) => {
              const bucketStart = min + index * bucketWidth;
              const bucketEnd = bucketStart + bucketWidth;
              const selected = bucketStart < displayValue[1] && bucketEnd > displayValue[0];
              return (
                <div
                  className={styles.bar({ selected })}
                  key={`${bucketStart}`}
                  style={{ height: `${(count / maxCount) * 100}%` }}
                />
              );
            })}
          </div>
        ) : null}
        <RangeSlider
          aria-label={t("components.price_range.label")}
          maxValue={max}
          minValue={min}
          onChange={setDragValue}
          onChangeEnd={(range) => {
            setDragValue(null);
            onChange(range);
          }}
          step={step}
          thumbLabels={[minimumLabel, maximumLabel]}
          value={displayValue}
        />
      </div>
      <div className={styles.inputs()}>
        <div className={styles.inputGroup()}>
          <span className={styles.inputLabel()}>{minimumLabel}</span>
          <PriceInput
            label={minimumLabel}
            local={localMin}
            onCommit={commitMin}
            onEdit={setEditingMin}
            onLocalChange={setLocalMin}
            prefix={currencySymbol}
            styles={styles}
          />
        </div>
        <div className={styles.inputGroup()}>
          <span className={styles.inputLabel()}>{maximumLabel}</span>
          <PriceInput
            label={maximumLabel}
            local={localMax}
            onCommit={commitMax}
            onEdit={setEditingMax}
            onLocalChange={setLocalMax}
            prefix={currencySymbol}
            styles={styles}
          />
        </div>
      </div>
    </div>
  );
}
